import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { variantKeys } from "@/hooks/useVariantQueries";
import { orderKeys } from "@/hooks/useOrderQueries";
import { cartKeys } from "@/hooks/useCartQueries";
import { productKeys } from "@/hooks/useProductQueries";
import { categoryKeys } from "@/hooks/useCategoryQueries";
import type { Product, ProductsResponse } from "@/api/productApi";
import { useAuthStore } from "@/stores/authStore";
import { hasRole } from "@/config/roles";

const EVENTS_URL = `${import.meta.env.VITE_API_URL}/events`;
const STOCK_DEBOUNCE_MS = 400;

interface StockUpdatePayload {
  type?: "cart-update" | "order-created" | "order-updated" | "global";
  timestamp?: number;
  productIds?: string[];
  userId?: string;
}

function isPageVisible(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "visible";
}

export const useStockEvents = () => {
  const queryClient = useQueryClient();
  const { userInfo } = useAuthStore();
  const stockDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingStockRef = useRef<StockUpdatePayload | null>(null);

  useEffect(() => {
    let source: EventSource | null = null;

    const connect = () => {
      if (source) source.close();
      source = new EventSource(EVENTS_URL);

      source.addEventListener("stock-updated", (event) => {
        if (!isPageVisible()) return;

        let data: StockUpdatePayload;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }

        // Merge with any pending payload (debounce) - treat as global if any event is global
        const prev = pendingStockRef.current;
        const isGlobalEvent =
          !data.type || data.type === "global" || (data.productIds?.length ?? 0) === 0;
        const wasGlobal =
          prev && (!prev.type || prev.type === "global" || (prev.productIds?.length ?? 0) === 0);
        const merged: StockUpdatePayload = {
          type: isGlobalEvent || wasGlobal ? "global" : data.type,
          productIds: [...new Set([...(prev?.productIds ?? []), ...(data.productIds ?? [])])],
          userId: data.userId ?? prev?.userId,
        };
        pendingStockRef.current = merged;

        if (stockDebounceRef.current) clearTimeout(stockDebounceRef.current);
        stockDebounceRef.current = setTimeout(() => {
          stockDebounceRef.current = null;
          const payload = pendingStockRef.current;
          pendingStockRef.current = null;
          if (!payload) return;

          const { type, productIds = [], userId } = payload;
          const pathname = window.location.pathname;
          const currentUserId = userInfo?.userId;
          const userRole = userInfo?.role;

          // 1. Categories & products: only on global changes (no targeted productIds)
          if (!type || type === "global" || productIds.length === 0) {
            queryClient.invalidateQueries({ queryKey: categoryKeys.all });
            queryClient.invalidateQueries({ queryKey: productKeys.all });
          }

          // 2. Variants: when stock changes, user is on the shop page,
          //    and affected products are visible in the cached product list
          if (productIds.length > 0 && pathname === "/shop") {
            const affectedSet = new Set(productIds);
            const cachedQueries = queryClient.getQueriesData<ProductsResponse | Product[]>({
              queryKey: productKeys.all,
            });

            const isAffected = cachedQueries.some(([, cached]) => {
              if (!cached) return false;
              const products: Product[] = Array.isArray(cached)
                ? cached
                : ((cached as ProductsResponse).products ?? []);
              return products.some((p) => affectedSet.has(p._id));
            });

            if (isAffected) {
              queryClient.invalidateQueries({ queryKey: variantKeys.all });
              queryClient.invalidateQueries({ queryKey: variantKeys.lists() });
              queryClient.invalidateQueries({ queryKey: [...variantKeys.all, "infinite"] });
            }
          }

          // 3. Orders: when the current user creates an order,
          //    or when an admin/manager (role >= 2) is on the orders page
          if (type === "order-created" || type === "order-updated") {
            const isOwnOrder = userId === currentUserId;
            const isManagerOnOrdersPage = hasRole(userRole, "sales") && pathname === "/orders";
            if (isOwnOrder || isManagerOnOrdersPage) {
              queryClient.invalidateQueries({ queryKey: orderKeys.all });
            }
          }
        }, STOCK_DEBOUNCE_MS);
      });

      source.addEventListener("cart-expired", (event) => {
        try {
          const data = JSON.parse(event.data);
          if (userInfo && data.userId === userInfo.userId) {
            queryClient.setQueryData(cartKeys.all, {
              items: [],
              itemCount: 0,
              totalPrice: 0,
              expiresAt: null,
            });
            queryClient.invalidateQueries({ queryKey: orderKeys.all });
            toast.warning("Your cart has been auto-submitted as an order", {
              description: "Please update the delivery details in your orders page.",
              duration: 10000,
            });
          }
        } catch {
          // ignore parse errors
        }
      });
    };

    const handleVisibilityChange = () => {
      if (isPageVisible()) {
        connect();
      } else {
        if (source) {
          source.close();
          source = null;
        }
      }
    };

    if (isPageVisible()) {
      connect();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (stockDebounceRef.current) clearTimeout(stockDebounceRef.current);
      if (source) source.close();
    };
  }, [queryClient, userInfo]);
};
